import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { ArrowLeft, Bell } from "lucide-react";
import Wordmark from "@/components/shared/Wordmark";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Personal email preferences for anyone signed in. Only the kinds of
 * email their school actually sends them are listed.
 */
export default function Preferences({ types }) {
  const { auth, routes } = usePage().props;
  const { data, setData, patch, processing } = useForm({
    notification_preferences: Object.fromEntries(types.map((t) => [t.key, t.enabled])),
  });

  const setPref = (key, value) => setData("notification_preferences", { ...data.notification_preferences, [key]: value });
  const submit = (e) => {
    e.preventDefault();
    patch("/preferences", { preserveScroll: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <Head title="Email preferences" />

      <div className="mx-auto max-w-2xl px-6 py-10 md:py-14">
        <div className="mb-8 flex items-center justify-between gap-4">
          <Link href={routes?.dashboard || "/"} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <Wordmark className="text-lg" />
        </div>

        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-foreground md:text-4xl">Email preferences</h1>
          {auth?.user?.email && (
            <p className="mt-2 text-sm text-muted-foreground">
              For <span className="text-foreground">{auth.user.email}</span>
            </p>
          )}
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Bell className="h-5 w-5 text-primary" />
              Email me
            </CardTitle>
            <CardDescription>Sign-in links and donation receipts always arrive.</CardDescription>
          </CardHeader>
          <CardContent>
            {types.length === 0 ? (
              <p className="text-sm leading-relaxed text-muted-foreground">
                There's nothing to manage right now. Your school decides which emails PagePledge sends, and anything it turns on will show up here.
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {types.map((t) => (
                    <li key={t.key} className="flex items-start gap-4 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{t.label}</p>
                        {t.description && <p className="mt-0.5 text-sm text-muted-foreground">{t.description}</p>}
                      </div>
                      <Switch
                        className="mt-0.5"
                        checked={!!data.notification_preferences[t.key]}
                        onChange={(checked) => setPref(t.key, checked)}
                        label={t.label}
                      />
                    </li>
                  ))}
                </ul>
                <Button type="submit" disabled={processing}>{processing ? "Saving…" : "Save preferences"}</Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
